import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';

const convertEditorStateToText = (editorState) => {
  if (!editorState) {
    console.warn('Editor state is missing or empty');
    return 'No content available';
  }
  
  try {
    if (editorState.toText) {
      return editorState.toText();
    } else if (editorState.toString) {
      return editorState.toString();
    }
    
    const content = document.querySelector('.editor-container .editor-input');
    if (content) {
      return content.textContent;
    }
    
    const editorContent = document.querySelector('[contenteditable="true"]');
    if (editorContent) {
      return editorContent.textContent;
    }
    
    console.warn('Could not find editor content');
    return 'No content available';
  } catch (error) {
    console.error('Error converting editor state to text:', error);
    return 'Error converting content';
  }
};

export const generatePdf = (editorState, title) => {
  return new Promise((resolve, reject) => {
    try {
      let textContent = '';
      let diagInfo = '';
      
      diagInfo += '===== DIAGNOSTIC INFORMATION =====\n\n';
      diagInfo += `Time: ${new Date().toISOString()}\n`;
      diagInfo += `EditorState type: ${typeof editorState}\n`;
      diagInfo += `EditorState available: ${editorState ? 'Yes' : 'No'}\n\n`;
      
      if (editorState) {
        diagInfo += 'EditorState properties:\n';
        try {
          const keys = Object.keys(editorState);
          diagInfo += keys.join(', ') + '\n\n';
        } catch (e) {
          diagInfo += `Error getting keys: ${e.message}\n\n`;
        }
        
        diagInfo += `toText method: ${typeof editorState.toText === 'function' ? 'Available' : 'Not available'}\n`;
        diagInfo += `toHTML method: ${typeof editorState.toHTML === 'function' ? 'Available' : 'Not available'}\n`;
        diagInfo += `toJSON method: ${typeof editorState.toJSON === 'function' ? 'Available' : 'Not available'}\n\n`;
      }
      
      // Log DOM information
      let editor = document.querySelector('[contenteditable="true"]');
      diagInfo += 'DOM Editor Information:\n';
      diagInfo += `Primary editor element found: ${editor ? 'Yes' : 'No'}\n`;
      
      // Try multiple selectors if the first one didn't work
      if (!editor) {
        diagInfo += 'Trying alternative selectors...\n';
        
        // Try common editor selectors
        const selectors = [
          '.editor-input',
          '.editor-container [contenteditable]',
          '.document-editor [contenteditable]',
          '.document-canvas [contenteditable]',
          '[role="textbox"]',
          '.public-DraftEditor-content',
          '.ql-editor',
          'div[data-lexical-editor]'
        ];
        
        for (const selector of selectors) {
          const el = document.querySelector(selector);
          if (el) {
            diagInfo += `Found element with selector: ${selector}\n`;
            editor = el;
            break;
          }
        }
        
        // If still not found, get ALL contenteditable elements
        if (!editor) {
          const allEditables = document.querySelectorAll('[contenteditable]');
          diagInfo += `Total contenteditable elements found: ${allEditables.length}\n`;
          
          if (allEditables.length > 0) {
            // Use the first one with content
            for (const el of allEditables) {
              if (el.textContent && el.textContent.trim() !== '') {
                editor = el;
                diagInfo += `Using contenteditable element with text content (length: ${el.textContent.length})\n`;
                break;
              }
            }
            
            // If no elements with content, just use the first one
            if (!editor && allEditables.length > 0) {
              editor = allEditables[0];
              diagInfo += 'Using first contenteditable element (no content)\n';
            }
          }
        }
      }
      
      if (editor) {
        diagInfo += `Editor innerHTML length: ${editor.innerHTML?.length || 0}\n`;
        diagInfo += `Editor textContent length: ${editor.textContent?.length || 0}\n\n`;
        
        // Sample of content (first 100 chars)
        if (editor.textContent) {
          const sample = editor.textContent.substring(0, 100);
          diagInfo += `Content sample: ${sample}${sample.length < editor.textContent.length ? '...' : ''}\n\n`;
        }
      } else {
        diagInfo += 'CRITICAL: No editor element could be found after trying multiple methods\n\n';
        
        // Last resort: try to find ANY element with text that looks like editor content
        const allElements = document.querySelectorAll('div, p, span');
        let longestTextElement = null;
        let longestText = '';
        
        for (const el of allElements) {
          if (el.textContent && 
              el.textContent.length > longestText.length && 
              !el.textContent.includes('DOCTYPE') &&
              !el.textContent.includes('<html>')) {
            longestText = el.textContent;
            longestTextElement = el;
          }
        }
        
        if (longestTextElement) {
          editor = longestTextElement;
          diagInfo += `Using element with longest text content (${longestText.length} chars)\n`;
          if (longestText) {
            const sample = longestText.substring(0, 100);
            diagInfo += `Sample: ${sample}${sample.length < longestText.length ? '...' : ''}\n\n`;
          }
        }
      }
      
      // Method 1: Try using the editorState's toText method
      if (editorState && editorState.toText) {
        textContent = editorState.toText();
        diagInfo += 'Content source: editorState.toText()\n\n';
      } 
      // Method 2: Try to get raw text from DOM
      else if (editor) {
        textContent = editor.textContent || editor.innerText;
        diagInfo += 'Content source: DOM element textContent\n\n';
      }
      
      // If we still don't have content, try JSON stringification as last resort
      if (!textContent || textContent.trim() === '') {
        // Try getting text content as a fallback
        diagInfo += 'Content source: JSON stringify fallback\n\n';
        try {
          if (editorState && typeof editorState === 'object') {
            // Only stringify relevant parts to avoid circular references
            const safeObj = {};
            try {
              if (editorState.root) safeObj.root = editorState.root;
              if (editorState.text) safeObj.text = editorState.text;
              textContent = JSON.stringify(safeObj, null, 2);
            } catch (e) {
              textContent = `Error stringifying: ${e.message}`;
              diagInfo += `JSON stringify error: ${e.message}\n`;
            }
          }
        } catch (e) {
          diagInfo += `Error accessing editorState: ${e.message}\n`;
        }
      }
      
      // Default text if everything else failed
      if (!textContent || textContent.trim() === '') {
        textContent = 'No content available';
        diagInfo += 'Content source: Default fallback message\n\n';
      }
      
      diagInfo += `Final content length: ${textContent.length} characters\n`;
      
      // DEBUGGING: First create a debug page with diagnostic information
      const debug = false; // Set to false to disable the diagnostic page
      
      // Clean up the text content to remove excessive whitespace but preserve structure
      textContent = textContent.replace(/\n{3,}/g, '\n\n'); // Replace excessive newlines
      
      // Try to detect if this is a structured document with sections
      const hasStructuredContent = textContent.includes('**') || 
                                  textContent.includes('Dear ') || 
                                  textContent.includes('Signature') ||
                                  textContent.includes('[') && textContent.includes(']') ||
                                  textContent.includes('*');
      
      // Add title - if we can detect a title in the content, use it
      let documentTitle = title;
      if (hasStructuredContent) {
        // Try to find a title in the content
        const lines = textContent.split('\n');
        for (let i = 0; i < Math.min(5, lines.length); i++) {
          const line = lines[i].trim();
          if (line && line.length > 3 && line.length < 50 && 
              !line.startsWith('[') && !line.startsWith('*') && 
              !line.includes('Dear ') && !line.includes('@')) {
            documentTitle = line;
            break;
          }
        }
      }
      
      // Create PDF document directly with jsPDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      if (debug) {
        // Add diagnostic page first
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.text('PDF Generation Diagnostic Page', 20, 20);
        
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
        pdf.line(20, 24, 190, 24);
        
        pdf.setFontSize(10);
        const diagLines = pdf.splitTextToSize(diagInfo, 170);
        pdf.text(diagLines, 20, 30);
        
        // Add a second page with the actual content
        pdf.addPage();
      }
      
      // Add document title
      pdf.setFontSize(18);
      pdf.setTextColor(0, 0, 0);
      pdf.text(documentTitle, 20, 20);
      
      // Add a line under the title
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(20, 24, 190, 24);
      
      // Add content with text wrapping
      pdf.setFontSize(12);
      
      // Handle long text content by splitting into paragraphs and paging as needed
      const paragraphs = textContent.split(/\r?\n/);  // Split by single line breaks to preserve format
      let yPosition = 30;
      const lineHeight = 7;
      const pageWidth = 170; // Width with margins
      
      // Check if we have a letter format with addresses
      const hasLetterFormat = textContent.includes('[') && textContent.includes(']') && 
                             (textContent.includes('Address') || textContent.includes('Code]'));
      
      if (hasLetterFormat) {
        // Special handling for business letter format
        let inAddress = false;
        let addressCount = 0;
        
        paragraphs.forEach(paragraph => {
          if (paragraph.trim() === '') {
            yPosition += lineHeight; // Add space for empty lines
            return;
          }
          
          // Check if we need a new page
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = 20;
          }
          
          // Check if this is part of an address block
          if (paragraph.includes('[') && paragraph.includes(']')) {
            if (!inAddress) {
              inAddress = true;
              addressCount++;
            }
            
            // If this is the sender's address (first address block)
            if (addressCount === 1) {
              pdf.setFont(undefined, 'normal');
              
              // Clean up the placeholder text
              let addressLine = paragraph.replace(/\[|\]/g, '');
              
              const lines = pdf.splitTextToSize(addressLine, pageWidth);
              pdf.text(lines, 20, yPosition);
              yPosition += lines.length * lineHeight;
            } 
            // If this is the recipient's address (second address block)
            else if (addressCount === 2) {
              pdf.setFont(undefined, 'normal');
              
              // Clean up the placeholder text
              let addressLine = paragraph.replace(/\[|\]/g, '');
              
              const lines = pdf.splitTextToSize(addressLine, pageWidth);
              pdf.text(lines, 20, yPosition);
              yPosition += lines.length * lineHeight;
            }
          }
          // Check for "Dear" line (salutation)
          else if (paragraph.includes('Dear')) {
            inAddress = false;
            
            pdf.setFont(undefined, 'normal');
            const lines = pdf.splitTextToSize(paragraph, pageWidth);
            pdf.text(lines, 20, yPosition);
            yPosition += lines.length * lineHeight + 5;
          }
          // Normal paragraph
          else {
            inAddress = false;
            
            const lines = pdf.splitTextToSize(paragraph, pageWidth);
            pdf.text(lines, 20, yPosition);
            yPosition += lines.length * lineHeight + 2;
          }
        });
      }
      // Check for contract formatting with sections and signatures
      else if (textContent.includes('**') || textContent.includes('Signature') || textContent.includes('Date:')) {
        // Special handling for contract-like documents with formatting
        paragraphs.forEach(paragraph => {
          if (paragraph.trim() === '') {
            yPosition += lineHeight; // Add space for empty lines
            return;
          }
          
          // Check if we need a new page
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = 20;
          }
          
          // Check for section headers (with ** formatting)
          if (paragraph.includes('**') && paragraph.length < 100) {
            pdf.setFontSize(14);
            pdf.setFont(undefined, 'bold');
            
            // Clean up the header text
            let headerText = paragraph.replace(/\*\*/g, '');
            if (headerText.endsWith(':')) {
              headerText = headerText.substring(0, headerText.length - 1);
            }
            
            const lines = pdf.splitTextToSize(headerText, pageWidth);
            pdf.text(lines, 20, yPosition);
            yPosition += lines.length * lineHeight + 2;
            
            // Reset font for normal text
            pdf.setFontSize(12);
            pdf.setFont(undefined, 'normal');
          } 
          // Check for signature lines
          else if (paragraph.includes('Signature') || paragraph.includes('Date:')) {
            pdf.setFont(undefined, 'bold');
            const signatureParts = paragraph.split('**');
            let signatureText = paragraph;
            
            if (signatureParts.length > 1) {
              signatureText = signatureParts.join('');
            }
            
            const lines = pdf.splitTextToSize(signatureText, pageWidth);
            pdf.text(lines, 20, yPosition);
            
            // Add a signature line
            yPosition += lines.length * lineHeight;
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.2);
            pdf.line(100, yPosition - 2, 190, yPosition - 2);
            yPosition += 5;
            
            // Reset font
            pdf.setFont(undefined, 'normal');
          }
          // Normal paragraph
          else {
            const lines = pdf.splitTextToSize(paragraph, pageWidth);
            pdf.text(lines, 20, yPosition);
            yPosition += lines.length * lineHeight + 2;
          }
        });
      } else {
        // Standard text handling for regular documents
        paragraphs.forEach(paragraph => {
          if (paragraph.trim() === '') {
            yPosition += lineHeight / 2; // Add space for empty lines
            return;
          }
          
          // Check if we need a new page
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = 20;
          }
          
          // Split paragraph text to fit page width
          const lines = pdf.splitTextToSize(paragraph, pageWidth);
          
          // Add the lines to the PDF
          pdf.text(lines, 20, yPosition);
          
          // Update position for next paragraph with appropriate spacing
          yPosition += lines.length * lineHeight + 2;
        });
      }
      
      // Save the PDF
      pdf.save(`${title.replace(/\s+/g, '_')}.pdf`);
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

export const generateDocx = (editorState, title) => {
  return new Promise((resolve, reject) => {
    try {
      const textContent = convertEditorStateToText(editorState);
      
      // Split text content by line breaks
      const paragraphs = textContent.split(/\r?\n/).filter(line => line.trim() !== '');
      
      // Create paragraphs for the docx document
      const docxParagraphs = paragraphs.map(paragraph => 
        new Paragraph({
          children: [
            new TextRun(paragraph)
          ]
        })
      );
      
      // Create a new Document
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: docxParagraphs
          }
        ]
      });
      
      // Generate the docx file
      Packer.toBlob(doc)
        .then(blob => {
          saveAs(blob, `${title.replace(/\s+/g, '_')}.docx`);
          console.log('DOCX generated successfully');
          resolve();
        })
        .catch(error => {
          console.error('Error packing DOCX:', error);
          reject(error);
        });
    } catch (error) {
      console.error('Error generating DOCX:', error);
      reject(error);
    }
  });
}; 